// panel-web/src/pages/RegistroBiologo.jsx
import { useEffect, useRef, useState } from 'react';
import api from '../api/client';
import { useFincaId } from '../context/FincaContext';

const MODOS   = ['Cronograma', 'Automático', 'Híbrido', 'Voleo'];
const NIVELES = ['Económico', 'Normal', 'Agresivo'];

const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
};
const hoyISO  = () => new Date().toISOString().split('T')[0];
const sacosDe = (kg) => (kg && kg > 0 ? (kg / 25) : 0);

function clasificarPctInicio(pct, modo) {
  if (pct == null) {
    return { clase: 'text-gray-400', bg: '', titulo: 'Sin datos' };
  }

  const diffAbs = Math.abs(pct - 100);
  const esExacto = diffAbs < 0.01;

  if (modo === 'Cronograma') {
    if (esExacto) {
      return {
        clase: 'text-gray-700',
        bg: '',
        titulo: 'Igual a la dieta (100%) — sin semáforo en modo Cronograma',
      };
    }
    return {
      clase: 'text-red-800 font-bold',
      bg: 'bg-red-100',
      titulo: `Modo Cronograma: diferencia del ${diffAbs.toFixed(1)}% con la dieta`,
    };
  }

  if (esExacto) {
    return {
      clase: 'text-green-800 font-bold',
      bg: 'bg-green-100',
      titulo: 'Igual a la dieta proyectada (100%)',
    };
  }
  if (pct < 100) {
    return {
      clase: 'text-red-800 font-bold',
      bg: 'bg-red-100',
      titulo: `Por debajo de la dieta (${pct.toFixed(1)}%)`,
    };
  }
  if (pct > 110) {
    return {
      clase: 'text-red-800 font-bold',
      bg: 'bg-red-100',
      titulo: `Por encima del 110% (${pct.toFixed(1)}%)`,
    };
  }
  return {
    clase: 'text-orange-800 font-bold',
    bg: 'bg-orange-100',
    titulo: `Dentro del margen (${pct.toFixed(1)}%) — entre 100% y 110%`,
  };
}

export default function RegistroBiologo() {
  const fincaId = useFincaId();
  const [sectores, setSectores]         = useState([]);
  const [sectorId, setSectorId]         = useState('');
  const [fecha, setFecha]               = useState(hoyISO());
  const [filas, setFilas]               = useState([]);
  const [tiposBalanceado, setTiposBal]  = useState([]);
  const [cargando, setCargando]         = useState(false);
  const [mensaje, setMensaje]           = useState(null);
  const [notificado, setNotificado]     = useState(false);
  const [mostrarSacos, setMostrarSacos] = useState(() => {
    const stored = localStorage.getItem('registroBiologo_mostrarSacos');
    return stored === null ? true : stored === 'true';
  });
  const gridRef                         = useRef(null);

  useEffect(() => {
    localStorage.setItem('registroBiologo_mostrarSacos', mostrarSacos);
  }, [mostrarSacos]);

  useEffect(() => {
    if (!fincaId) return;
    (async () => {
      try {
        const { data } = await api.get(`/sectores/finca/${fincaId}`);
        setSectores(data || []);
        if (data?.[0]) setSectorId((prev) => prev || data[0].id);
      } catch (err) {
        console.error('[RegBio] error sectores:', err);
        setMensaje({
          tipo: 'error',
          texto: `No se pudieron cargar los sectores (HTTP ${err.response?.status || '?'})`,
        });
      }
      try {
        const { data } = await api.get('/tipos-balanceado');
        setTiposBal(data || []);
      } catch (err) {
        console.error('[RegBio] error tipos-balanceado:', err);
      }
    })();
  }, [fincaId]);

  useEffect(() => { setNotificado(false); }, [sectorId, fecha]);

  useEffect(() => {
    if (sectorId && fecha) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId, fecha]);

  const cambiarDia = (delta) => {
    const [a, m, d] = fecha.split('-').map(Number);
    const dt = new Date(a, m - 1, d);
    dt.setDate(dt.getDate() + delta);
    setFecha(dt.toISOString().split('T')[0]);
  };

  const irHoy = () => setFecha(hoyISO());

  const cargar = async () => {
    setCargando(true);
    setMensaje(null);
    try {
      const { data } = await api.get('/registro-biologo', {
        params: { sector_id: sectorId, fecha },
      });
      const lista = Array.isArray(data) ? data : [];
      setFilas(lista);
      if (lista.length === 0) {
        setMensaje({ tipo: 'error', texto: 'El sector no tiene piscinas activas.' });
      }
    } catch (err) {
      const status = err.response?.status;
      const detalle = err.response?.data?.error || err.message || 'Error desconocido';
      setMensaje({ tipo: 'error', texto: `Error al cargar (HTTP ${status || '?'}): ${detalle}` });
      setFilas([]);
    } finally {
      setCargando(false);
    }
  };

  const setCampo = (idx, campo, valor) => {
    setFilas((prev) => {
      const copia = [...prev];
      const fila  = { ...copia[idx], [campo]: valor };

      if (campo === 'sobrante_tolva_kg' || campo === 'dieta_proyectada_kg') {
        const s = toNum(fila.sobrante_tolva_kg);
        const d = toNum(fila.dieta_proyectada_kg);
        if (s != null && d != null) {
          const a = d - s;
          fila.alimento_abastecido_kg = a.toFixed(2);
          fila.cantidad_total_kg      = (s + a).toFixed(2);
        } else {
          fila.alimento_abastecido_kg = '';
          fila.cantidad_total_kg      = '';
        }
      }

      if (campo === 'alimento_abastecido_kg') {
        const s = toNum(fila.sobrante_tolva_kg);
        const a = toNum(fila.alimento_abastecido_kg);
        if (s != null && a != null) {
          fila.cantidad_total_kg = (s + a).toFixed(2);
        }
      }

      copia[idx] = fila;
      return copia;
    });
  };

  const guardarFila = async (fila) => {
    try {
      const payload = {
        piscina_id: fila.piscina_id,
        sector_id:  sectorId,
        fecha,
        sobrante_tolva_kg:      toNum(fila.sobrante_tolva_kg),
        alimento_abastecido_kg: toNum(fila.alimento_abastecido_kg),
        cantidad_total_kg:      toNum(fila.cantidad_total_kg),
        dieta_proyectada_kg:    toNum(fila.dieta_proyectada_kg),
        tipo_balanceado:        fila.tipo_balanceado || null,
        hora_inicio:            fila.hora_inicio || null,
        hora_fin:               fila.hora_fin    || null,
        modo:                   fila.modo  || null,
        nivel:                  fila.nivel || null,
        observaciones:          fila.observaciones || null,
      };
      const { data } = await api.post('/registro-biologo', payload);
      setFilas((prev) => {
        const copia = [...prev];
        const i = copia.findIndex((f) => f.piscina_id === fila.piscina_id);
        if (i >= 0) copia[i] = { ...copia[i], registro_id: data.id };
        return copia;
      });
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al guardar' });
    }
  };

  const onKeyDown = (e, idx, campo) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      guardarFila(filas[idx]);
      const next = gridRef.current?.querySelector(
        `[data-idx="${idx + 1}"][data-campo="${campo}"]`
      );
      if (next) next.focus();
    }
  };

  const exportarExcel = async () => {
    if (!sectorId || !fecha) return;
    try {
      const res = await api.get('/registro-biologo/exportar/excel', {
        params: { sector_id: sectorId, fecha },
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      let nombre = `registro_biologo_${fecha}.xlsx`;
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) nombre = match[1];

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setMensaje({ tipo: 'error', texto: 'Error al exportar Excel' });
    }
  };

  const notificarDietaLista = async () => {
    try {
      await api.post('/alertas/dieta-lista', { sector_id: sectorId, fecha });
      setNotificado(true);
      setMensaje({ tipo: 'ok', texto: '✅ Notificación enviada al monitorista' });
      setTimeout(() => setMensaje(null), 2500);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al notificar' });
    }
  };

  let totalSobrante = 0, totalAbast = 0, totalDia = 0, totalDieta = 0;
  filas.forEach((f) => {
    totalSobrante += toNum(f.sobrante_tolva_kg)      || 0;
    totalAbast    += toNum(f.alimento_abastecido_kg) || 0;
    totalDia      += toNum(f.cantidad_total_kg)      || 0;
    totalDieta    += toNum(f.dieta_proyectada_kg)    || 0;
  });

  const totalPctInicio = totalDieta > 0 ? (totalDia / totalDieta) * 100 : null;
  const totalPctClase  = clasificarPctInicio(totalPctInicio, 'Automático');

  const todasListas = filas.length > 0 && filas.every(
    (f) => toNum(f.dieta_proyectada_kg) != null && toNum(f.dieta_proyectada_kg) > 0
  );

  const colspanTotal = mostrarSacos ? 17 : 13;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-primary">🔬 Registro Biólogo</h1>
        <div className="flex gap-2">
          <button
            onClick={notificarDietaLista}
            disabled={!todasListas || notificado}
            title={
              notificado
                ? 'Ya notificado'
                : todasListas
                ? 'Notificar al monitorista que la dieta está completa'
                : 'Aún faltan piscinas con dieta proyectada'
            }
            className={`px-4 py-2 rounded text-sm font-medium transition ${
              notificado
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : todasListas
                ? 'bg-green-600 text-white hover:bg-green-700 shadow'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {notificado ? '✔ Dieta actualizada' : '✅ Dieta actualizada'}
          </button>
          <button
            onClick={exportarExcel}
            disabled={!filas.length}
            className="px-4 py-2 bg-green-700 text-white rounded text-sm hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            📥 Exportar Excel
          </button>
        </div>
      </div>

      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col">
          <span className="text-xs text-gray-600 mb-1">Fecha</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => cambiarDia(-1)}
              className="px-3 py-1.5 bg-primary text-white rounded hover:bg-blue-800 text-sm font-bold"
              title="Día anterior"
            >
              ←
            </button>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="px-3 py-1.5 border rounded text-sm"
            />
            <button
              type="button"
              onClick={() => cambiarDia(1)}
              className="px-3 py-1.5 bg-primary text-white rounded hover:bg-blue-800 text-sm font-bold"
              title="Día siguiente"
            >
              →
            </button>
            <button
              type="button"
              onClick={irHoy}
              className="px-3 py-1.5 border border-primary text-primary rounded hover:bg-blue-50 text-xs font-medium"
              title="Ir a hoy"
            >
              Hoy
            </button>
          </div>
        </div>

        <label className="flex flex-col">
          <span className="text-xs text-gray-600 mb-1">Sector</span>
          <select value={sectorId}
            onChange={(e) => setSectorId(e.target.value)}
            className="px-3 py-1.5 border rounded text-sm bg-white">
            <option value="">-- Selecciona --</option>
            {sectores.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </label>

        <button onClick={cargar}
          className="px-3 py-1.5 bg-primary text-white rounded text-sm hover:bg-blue-800">
          Refrescar
        </button>

        <label className="flex items-center gap-2 px-3 py-1.5 border rounded cursor-pointer hover:bg-gray-50 select-none">
          <input
            type="checkbox"
            checked={mostrarSacos}
            onChange={(e) => setMostrarSacos(e.target.checked)}
            className="w-4 h-4 cursor-pointer accent-primary"
          />
          <span className="text-sm text-gray-700">Mostrar sacos</span>
        </label>

        {!todasListas && filas.length > 0 && (
          <div className="text-xs text-orange-600 bg-orange-50 px-3 py-1.5 rounded border border-orange-200">
            ⏳ Faltan piscinas con dieta proyectada
          </div>
        )}
        {todasListas && !notificado && (
          <div className="text-xs text-green-700 bg-green-50 px-3 py-1.5 rounded border border-green-200">
            ✔ Todas las piscinas tienen dieta — puedes notificar
          </div>
        )}
      </div>

      {mensaje && (
        <div className={`mb-3 p-2 rounded text-sm ${
          mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {mensaje.texto}
        </div>
      )}

      <div className="mb-3 bg-white border border-gray-200 rounded shadow-sm px-4 py-2 text-xs">
        <div className="font-semibold text-gray-700 mb-1">
          Semáforo <span className="text-blue-700">% Inicio Día</span>:
        </div>
        <div className="flex flex-col md:flex-row md:items-start gap-3">
          <div className="flex-1">
            <div className="text-gray-600 font-medium mb-1">
              Modos <strong>Automático</strong> / <strong>Híbrido</strong> / <strong>Voleo</strong>:
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-green-100 border border-green-400" />
                <span className="text-gray-700"><strong>Verde</strong> — = 100%</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-orange-100 border border-orange-400" />
                <span className="text-gray-700"><strong>Naranja</strong> — 100% a 110%</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-400" />
                <span className="text-gray-700"><strong>Rojo</strong> — &lt;100% o &gt;110%</span>
              </span>
            </div>
          </div>

          <div>
            <div className="text-gray-600 font-medium mb-1">
              Modo <strong>Cronograma</strong>:
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded border border-gray-300 bg-white" />
                <span className="text-gray-700"><strong>Sin color</strong> — = 100%</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-400" />
                <span className="text-gray-700"><strong>Rojo</strong> — cualquier desviación</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {cargando ? (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">Cargando...</div>
      ) : (
        <div className="bg-white rounded shadow overflow-x-auto" ref={gridRef}>
          <table className="w-full text-xs">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-2 py-1 border" rowSpan={2}>Psc</th>
                <th className="px-2 py-1 border" rowSpan={2}>Has</th>
                <th className="px-2 py-1 border" colSpan={mostrarSacos ? 2 : 1}>Sobrante en tolva</th>
                <th className="px-2 py-1 border" colSpan={mostrarSacos ? 2 : 1}>Alimento abastecido</th>
                <th className="px-2 py-1 border" colSpan={mostrarSacos ? 2 : 1}>Cantidad total inicio día</th>
                <th className="px-2 py-1 border bg-blue-50" rowSpan={2}>% Inicio Día</th>
                <th className="px-2 py-1 border" colSpan={mostrarSacos ? 2 : 1}>Dieta proyectada</th>
                <th className="px-2 py-1 border" rowSpan={2}>Tipo de balanceado</th>
                <th className="px-2 py-1 border" colSpan={2}>Horario de alimentación</th>
                <th className="px-2 py-1 border" rowSpan={2}>Modo</th>
                <th className="px-2 py-1 border" rowSpan={2}>Nivel</th>
                <th className="px-2 py-1 border" rowSpan={2}>Observaciones</th>
              </tr>
              <tr>
                <th className="px-2 py-1 border">Kg</th>
                {mostrarSacos && <th className="px-2 py-1 border">Sacos</th>}
                <th className="px-2 py-1 border">Kg</th>
                {mostrarSacos && <th className="px-2 py-1 border">Sacos</th>}
                <th className="px-2 py-1 border">Kg</th>
                {mostrarSacos && <th className="px-2 py-1 border">Sacos</th>}
                <th className="px-2 py-1 border">Kg</th>
                {mostrarSacos && <th className="px-2 py-1 border">Sacos</th>}
                <th className="px-2 py-1 border">Inicio</th>
                <th className="px-2 py-1 border">Fin</th>
              </tr>
            </thead>

            <tbody>
              {filas.map((f, idx) => {
                const sobrante = toNum(f.sobrante_tolva_kg);
                const abast    = toNum(f.alimento_abastecido_kg);
                const total    = toNum(f.cantidad_total_kg);
                const dieta    = toNum(f.dieta_proyectada_kg);
                // ✅ FIX: Bloquea el nivel si es Cronograma O Voleo
                const nivelDisabled = f.modo === 'Cronograma' || f.modo === 'Voleo';
                const sinDieta = dieta == null || dieta <= 0;

                const pctInicio = (dieta > 0 && total != null)
                  ? (total / dieta) * 100
                  : null;
                const pctClase = clasificarPctInicio(pctInicio, f.modo);

                return (
                  <tr key={f.piscina_id} className={`border-t hover:bg-blue-50/40 ${sinDieta ? 'bg-red-50/40' : ''}`}>
                    <td className="px-2 py-1 border font-medium whitespace-nowrap">{f.piscina_codigo}</td>
                    <td className="px-2 py-1 border text-right text-gray-600">{f.area_ha ?? '-'}</td>

                    <td className="px-1 py-0.5 border">
                      <input type="number" step="0.01"
                        data-idx={idx} data-campo="sobrante_tolva_kg"
                        value={f.sobrante_tolva_kg ?? ''}
                        onChange={(e) => setCampo(idx, 'sobrante_tolva_kg', e.target.value)}
                        onBlur={() => guardarFila(filas[idx])}
                        onKeyDown={(e) => onKeyDown(e, idx, 'sobrante_tolva_kg')}
                        className="w-20 px-1 py-0.5 text-right border rounded" />
                    </td>
                    {mostrarSacos && (
                      <td className="px-2 py-1 border text-right text-gray-500 bg-gray-50">
                        {sobrante > 0 ? sacosDe(sobrante).toFixed(2) : '-'}
                      </td>
                    )}

                    <td className="px-1 py-0.5 border">
                      <input type="number" step="0.01"
                        data-idx={idx} data-campo="alimento_abastecido_kg"
                        value={f.alimento_abastecido_kg ?? ''}
                        onChange={(e) => setCampo(idx, 'alimento_abastecido_kg', e.target.value)}
                        onBlur={() => guardarFila(filas[idx])}
                        onKeyDown={(e) => onKeyDown(e, idx, 'alimento_abastecido_kg')}
                        title="Auto: Dieta − Sobrante (puedes sobrescribir)"
                        className="w-20 px-1 py-0.5 text-right border rounded" />
                    </td>
                    {mostrarSacos && (
                      <td className="px-2 py-1 border text-right text-gray-500 bg-gray-50">
                        {abast > 0 ? sacosDe(abast).toFixed(2) : '-'}
                      </td>
                    )}

                    <td className="px-1 py-0.5 border">
                      <input type="number" step="0.01"
                        data-idx={idx} data-campo="cantidad_total_kg"
                        value={f.cantidad_total_kg ?? ''}
                        onChange={(e) => setCampo(idx, 'cantidad_total_kg', e.target.value)}
                        onBlur={() => guardarFila(filas[idx])}
                        onKeyDown={(e) => onKeyDown(e, idx, 'cantidad_total_kg')}
                        title="Auto: Sobrante + Abastecido (puedes sobrescribir)"
                        className="w-20 px-1 py-0.5 text-right border rounded" />
                    </td>
                    {mostrarSacos && (
                      <td className="px-2 py-1 border text-right text-gray-500 bg-gray-50">
                        {total > 0 ? sacosDe(total).toFixed(2) : '-'}
                      </td>
                    )}

                    <td
                      title={pctClase.titulo}
                      className={`px-2 py-1 border text-center whitespace-nowrap ${pctClase.bg} ${pctClase.clase}`}
                    >
                      {pctInicio != null ? `${pctInicio.toFixed(1)}%` : '-'}
                    </td>

                    <td className="px-1 py-0.5 border">
                      <input type="number" step="0.01"
                        data-idx={idx} data-campo="dieta_proyectada_kg"
                        value={f.dieta_proyectada_kg ?? ''}
                        onChange={(e) => setCampo(idx, 'dieta_proyectada_kg', e.target.value)}
                        onBlur={() => guardarFila(filas[idx])}
                        onKeyDown={(e) => onKeyDown(e, idx, 'dieta_proyectada_kg')}
                        className="w-20 px-1 py-0.5 text-right border rounded" />
                    </td>
                    {mostrarSacos && (
                      <td className="px-2 py-1 border text-right text-gray-500 bg-gray-50">
                        {dieta > 0 ? sacosDe(dieta).toFixed(2) : '-'}
                      </td>
                    )}

                    <td className="px-1 py-0.5 border">
                      <select value={f.tipo_balanceado}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setCampo(idx, 'tipo_balanceado', valor);
                          setTimeout(() => guardarFila({ ...filas[idx], tipo_balanceado: valor }), 0);
                        }}
                        className="w-36 px-1 py-0.5 border rounded text-xs bg-white">
                        <option value="">--</option>
                        {tiposBalanceado.map((t) => (
                          <option key={t.id} value={t.nombre}>{t.nombre}</option>
                        ))}
                      </select>
                    </td>

                    <td className="px-1 py-0.5 border">
                      <input type="time"
                        data-idx={idx} data-campo="hora_inicio"
                        value={f.hora_inicio ?? ''}
                        onChange={(e) => setCampo(idx, 'hora_inicio', e.target.value)}
                        onBlur={() => guardarFila(filas[idx])}
                        className="px-1 py-0.5 border rounded text-xs" />
                    </td>
                    <td className="px-1 py-0.5 border">
                      <input type="time"
                        data-idx={idx} data-campo="hora_fin"
                        value={f.hora_fin ?? ''}
                        onChange={(e) => setCampo(idx, 'hora_fin', e.target.value)}
                        onBlur={() => guardarFila(filas[idx])}
                        className="px-1 py-0.5 border rounded text-xs" />
                    </td>

                    {/* Modo */}
                    <td className="px-1 py-0.5 border">
                      <select value={f.modo}
                        onChange={(e) => {
                          const nuevoModo  = e.target.value;
                          // ✅ FIX: Limpia el nivel si es Cronograma o Voleo
                          const esModoSinNivel = nuevoModo === 'Cronograma' || nuevoModo === 'Voleo';
                          const nuevoNivel = esModoSinNivel ? '' : filas[idx].nivel;
                          setCampo(idx, 'modo', nuevoModo);
                          if (esModoSinNivel) setCampo(idx, 'nivel', '');
                          setTimeout(
                            () => guardarFila({ ...filas[idx], modo: nuevoModo, nivel: nuevoNivel }),
                            0
                          );
                        }}
                        className="w-40 px-1 py-0.5 border rounded text-xs bg-white">
                        <option value="">--</option>
                        {MODOS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </td>

                    {/* Nivel */}
                    <td className="px-1 py-0.5 border">
                      <select value={f.nivel}
                        disabled={nivelDisabled}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setCampo(idx, 'nivel', valor);
                          setTimeout(() => guardarFila({ ...filas[idx], nivel: valor }), 0);
                        }}
                        className={`w-28 px-1 py-0.5 border rounded text-xs ${
                          nivelDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'
                        }`}>
                        <option value="">--</option>
                        {NIVELES.map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </td>

                    {/* Observaciones */}
                    <td className="px-1 py-0.5 border">
                      <input type="text"
                        data-idx={idx} data-campo="observaciones"
                        value={f.observaciones ?? ''}
                        onChange={(e) => setCampo(idx, 'observaciones', e.target.value)}
                        onBlur={() => guardarFila(filas[idx])}
                        onKeyDown={(e) => onKeyDown(e, idx, 'observaciones')}
                        className="w-64 px-1 py-0.5 border rounded text-xs" />
                    </td>
                  </tr>
                );
              })}

              {filas.length === 0 && (
                <tr>
                  <td colSpan={colspanTotal} className="px-3 py-6 text-center text-gray-500">
                    No hay piscinas para mostrar.
                  </td>
                </tr>
              )}
            </tbody>

            {filas.length > 0 && (
              <tfoot className="bg-gray-100 font-semibold">
                <tr>
                  <td className="px-2 py-1 border" colSpan={2}>TOTALES</td>
                  <td className="px-2 py-1 border text-right">{totalSobrante.toFixed(2)}</td>
                  {mostrarSacos && (
                    <td className="px-2 py-1 border text-right">{sacosDe(totalSobrante).toFixed(2)}</td>
                  )}
                  <td className="px-2 py-1 border text-right">{totalAbast.toFixed(2)}</td>
                  {mostrarSacos && (
                    <td className="px-2 py-1 border text-right">{sacosDe(totalAbast).toFixed(2)}</td>
                  )}
                  <td className="px-2 py-1 border text-right">{totalDia.toFixed(2)}</td>
                  {mostrarSacos && (
                    <td className="px-2 py-1 border text-right">{sacosDe(totalDia).toFixed(2)}</td>
                  )}
                  <td
                    title={totalPctClase.titulo}
                    className={`px-2 py-1 border text-center whitespace-nowrap ${totalPctClase.bg} ${totalPctClase.clase}`}
                  >
                    {totalPctInicio != null ? `${totalPctInicio.toFixed(1)}%` : '-'}
                  </td>
                  <td className="px-2 py-1 border text-right">{totalDieta.toFixed(2)}</td>
                  {mostrarSacos && (
                    <td className="px-2 py-1 border text-right">{sacosDe(totalDieta).toFixed(2)}</td>
                  )}
                  <td className="px-2 py-1 border" colSpan={6}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}